import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { tierPillClass, tierLabel } from "../../lib/adminTier";

const TIERS = ["ospite", "amico", "famiglia", "padrone"];
const PAGE_SIZE = 50;

const fmtNumber = (n) => Number(n || 0).toLocaleString();
const fmtMonthYear = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch (_) {
    return "—";
  }
};

const useDebounce = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const SkeletonRow = () => (
  <tr className="border-b border-napoli-gold/15">
    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <td key={i} className="px-4 py-4">
        <div className="h-4 bg-napoli-marble animate-pulse" />
      </td>
    ))}
  </tr>
);

const CustomerCardMobile = ({ c }) => (
  <Link
    to={`/admin/customer/${c.id}`}
    data-testid={`customer-card-${c.id}`}
    className="block bg-napoli-marble border border-napoli-gold/20 p-4 hover:border-napoli-oxblood/40 transition-colors"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-body font-semibold text-[14px] text-napoli-espresso truncate">
          {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
        </p>
        <p className="font-body text-[11px] text-napoli-espresso-soft truncate mt-0.5">
          {c.email_masked || c.phone_masked || "—"}
        </p>
      </div>
      <span className={tierPillClass(c.tier)}>{tierLabel(c.tier)}</span>
    </div>
    <div className="mt-3 flex items-baseline gap-3">
      <span className="font-display font-bold text-[24px] text-napoli-gold leading-none tabular-nums">
        {fmtNumber(c.points_balance)} pts
      </span>
      <span className="font-body text-[11px] italic text-napoli-espresso-soft">
        lifetime {fmtNumber(c.lifetime_points)}
      </span>
    </div>
    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 font-body text-[11px] text-napoli-espresso-soft">
      <span>Joined {fmtMonthYear(c.created_at)}</span>
      <span>· {c.last_activity_at ? fmtMonthYear(c.last_activity_at) : "No activity"}</span>
      <span>· {c.total_visits} visits</span>
    </div>
  </Link>
);

const AdminCustomersPage = () => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [tier, setTier] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterCounts, setFilterCounts] = useState({ all: 0, ospite: 0, amico: 0, famiglia: 0, padrone: 0 });
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [appending, setAppending] = useState(false);
  const [err, setErr] = useState(null);

  const reqIdRef = useRef(0);

  const fetchPage = async (replace, currentOffset) => {
    const reqId = ++reqIdRef.current;
    if (replace) setLoading(true);
    else setAppending(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(currentOffset),
        sort: "lifetime_desc",
      });
      if (debouncedSearch && debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (tier) params.set("tier", tier);
      const { data } = await api.get(`/admin/customers?${params}`);
      if (reqId !== reqIdRef.current) return; // stale
      setTotal(data.total || 0);
      setFilterCounts(data.filter_counts || filterCounts);
      setHasMore(!!data.has_more);
      setItems((prev) => (replace ? data.customers : [...prev, ...data.customers]));
      setOffset(currentOffset + (data.customers?.length || 0));
    } catch (e) {
      setErr("Couldn't load customers. Try again.");
    } finally {
      if (reqId === reqIdRef.current) {
        setLoading(false);
        setAppending(false);
      }
    }
  };

  useEffect(() => {
    fetchPage(true, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, tier]);

  const allCount = filterCounts.all ?? 0;

  const FilterPill = ({ value, label, count, testId }) => {
    const active = tier === value;
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={() => setTier(value)}
        className={[
          "inline-flex items-center gap-2 px-3 py-1.5 font-body font-medium text-[12px] tracking-[0.1em] uppercase border transition-colors",
          active
            ? "bg-napoli-oxblood text-napoli-cream border-napoli-oxblood"
            : "bg-napoli-cream-warm text-napoli-espresso border-napoli-gold/40 hover:border-napoli-oxblood/40",
        ].join(" ")}
      >
        {label}
        <span className={`tabular-nums font-semibold ${active ? "text-napoli-cream/85" : "text-napoli-espresso-soft"}`}>
          {count}
        </span>
      </button>
    );
  };

  return (
    <section data-testid="admin-customers-page" className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
          All customers
        </p>
        <h1 className="mt-2 font-display font-bold text-[28px] sm:text-[32px] text-napoli-espresso leading-tight">
          Your customer base.
        </h1>
        <p className="mt-1 font-body italic text-[14px] text-napoli-espresso-soft">
          {fmtNumber(allCount)} customer{allCount === 1 ? "" : "s"} across all tiers.
        </p>
      </div>

      {/* Filter bar */}
      <div
        data-testid="admin-customers-filter-bar"
        className="bg-napoli-marble border border-napoli-gold/20 px-4 py-4 flex flex-col md:flex-row md:items-center gap-3"
      >
        <div className="relative md:max-w-md md:flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-napoli-espresso-soft" />
          <input
            data-testid="admin-customers-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full bg-napoli-cream-warm border border-napoli-gold/40 pl-9 pr-3 py-2.5 font-body text-[14px] text-napoli-espresso placeholder:text-napoli-espresso-soft/60 focus:outline-none focus:border-napoli-oxblood transition-colors"
          />
        </div>
        <div className="flex flex-wrap gap-2 md:justify-end md:flex-1">
          <FilterPill value={null} label="All" count={allCount} testId="filter-tier-all" />
          {TIERS.map((t) => (
            <FilterPill
              key={t}
              value={t}
              label={tierLabel(t)}
              count={filterCounts[t] ?? 0}
              testId={`filter-tier-${t}`}
            />
          ))}
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden mt-4 space-y-3" data-testid="admin-customers-cards">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 bg-napoli-marble animate-pulse" />
          ))
        ) : err ? (
          <p className="font-body italic text-napoli-oxblood text-center py-8">{err}</p>
        ) : items.length === 0 ? (
          <div data-testid="admin-customers-empty" className="bg-napoli-marble border border-napoli-gold/20 p-6 text-center font-body italic text-[13px] text-napoli-espresso-soft">
            No customers match your filter.
          </div>
        ) : (
          items.map((c) => <CustomerCardMobile key={c.id} c={c} />)
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block mt-4 bg-napoli-marble border border-napoli-gold/20">
        <table className="w-full" data-testid="admin-customers-table">
          <thead>
            <tr className="border-b border-napoli-gold/30">
              {[
                "Name",
                "Tier",
                "Points",
                "Lifetime",
                "Joined",
                "Last activity",
                "Visits",
                "",
              ].map((h, i) => (
                <th
                  key={i}
                  className="text-left px-4 py-3 font-body font-semibold text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
            ) : err ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center font-body italic text-napoli-oxblood">
                  {err}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  data-testid="admin-customers-empty-row"
                  className="px-4 py-10 text-center font-body italic text-[13px] text-napoli-espresso-soft"
                >
                  No customers match your filter.
                </td>
              </tr>
            ) : (
              items.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`customer-row-${c.id}`}
                  className="border-b border-napoli-gold/15 last:border-0 hover:bg-napoli-cream-warm/60 transition-colors cursor-pointer"
                  onClick={(e) => {
                    // ignore clicks on inner links
                    if (e.target.closest("a")) return;
                    window.location.href = `/admin/customer/${c.id}`;
                  }}
                >
                  <td className="px-4 py-3">
                    <p className="font-body font-semibold text-[14px] text-napoli-espresso">
                      {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                    </p>
                    <p className="font-body text-[11px] text-napoli-espresso-soft mt-0.5">
                      {c.email_masked || c.phone_masked || "—"}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={tierPillClass(c.tier)}>{tierLabel(c.tier)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-body font-bold text-[14px] text-napoli-gold tabular-nums">
                      {fmtNumber(c.points_balance)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-body font-medium text-[13px] text-napoli-espresso-soft tabular-nums">
                    {fmtNumber(c.lifetime_points)}
                  </td>
                  <td className="px-4 py-3 font-body text-[12px] text-napoli-espresso-soft">
                    {fmtMonthYear(c.created_at)}
                  </td>
                  <td className="px-4 py-3 font-body text-[12px] text-napoli-espresso-soft">
                    {c.last_activity_at ? fmtMonthYear(c.last_activity_at) : "Never"}
                  </td>
                  <td className="px-4 py-3 font-body text-[13px] text-napoli-espresso tabular-nums">
                    {c.total_visits}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/customer/${c.id}`}
                      data-testid={`customer-view-${c.id}`}
                      className="inline-flex items-center gap-1 font-body font-medium text-[12px] tracking-[0.05em] text-napoli-oxblood hover:text-napoli-oxblood-dark"
                    >
                      View <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {hasMore && !loading ? (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            data-testid="admin-customers-load-more"
            onClick={() => fetchPage(false, offset)}
            disabled={appending}
            className="inline-flex items-center gap-2 bg-napoli-oxblood text-napoli-cream px-8 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
          >
            {appending ? <><Loader2 size={14} className="animate-spin" /> Loading…</> : "Load more"}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default AdminCustomersPage;
